import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';

import { Autocomplete } from '@/components/Inputs';
import {
  PLACES_MIN_QUERY_LENGTH,
  searchPlaces,
  type PlaceSuggestion,
} from '@/utils/placesAutocomplete';
import { useDebounce } from '@/utils/use-debounce';

import { LOCATION_OPTIONS } from '../eventWizard.constants';
import type { LocationStop } from '../eventWizard.types';
import s from '../EventWizardModal.module.scss';

import { SortableLocationRow } from './SortableLocationRow';

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

interface StepLocationProps {
  locationQuery: string;
  onLocationQueryChange: (value: string) => void;
  locations: LocationStop[];
  onAddLocation: (value: string | null) => void;
  onRemoveLocation: (id: number) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function StepLocation({
  locationQuery,
  onLocationQueryChange,
  locations,
  onAddLocation,
  onRemoveLocation,
  onDragEnd,
}: StepLocationProps) {
  const [places, setPlaces] = useState<PlaceSuggestion[]>([]);
  const debouncedQuery = useDebounce(locationQuery);
  const isSearching = debouncedQuery.trim().length >= PLACES_MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!isSearching) return;

    const controller = new AbortController();
    searchPlaces(debouncedQuery, controller.signal).then((result) => {
      if (!controller.signal.aborted) setPlaces(result);
    });

    return () => controller.abort();
  }, [debouncedQuery, isSearching]);

  const locationSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <>
      <div className={s.stepHeader}>
        <h3 className={s.stepTitle}>Where will it take place?</h3>
        <p className={s.stepSubtitle}>Please add one or more locations, or skip for now.</p>
      </div>
      <div className={s.fields}>
        <Autocomplete
          label="Location"
          placeholder="Port Hercule, Monaco"
          value={locationQuery}
          onChange={onLocationQueryChange}
          options={isSearching ? places : LOCATION_OPTIONS}
          onItemSelect={onAddLocation}
        />
        {locations.length > 0 && (
          <div className={s.rowList}>
            <div className={s.listCount}>
              {locations.length} location{locations.length !== 1 ? 's' : ''} added
            </div>
            <DndContext
              sensors={locationSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={locations} strategy={verticalListSortingStrategy}>
                {locations.map((stop, idx) => (
                  <SortableLocationRow
                    key={stop.id}
                    id={stop.id}
                    name={stop.name}
                    index={idx}
                    onRemove={() => onRemoveLocation(stop.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </>
  );
}
